import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Warning,
  DirectionsCar,
  Build,
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Announcement,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { equipmentAPI, announcementAPI } from '../../services/api';
import { dashboardAPI, DashboardData, EquipmentStats, CalendarEvent } from '../../api/dashboard';
import { Equipment } from '../../types/equipment';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [isLocked, setIsLocked] = useState(true);
  const [announcementData, setAnnouncementData] = useState({
    maintenance_notice: '',
    new_features: '',
    usage_reminder: ''
  });
  
  // Dashboard API 數據
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [equipmentStats, setEquipmentStats] = useState<EquipmentStats>({
    available_count: 0,
    rented_count: 0,
    maintenance_count: 0,
    total_count: 0
  });
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [showDateDetail, setShowDateDetail] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>([]);

  // 使用真實 API 數據

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('開始載入儀表板數據...');
        
        // 使用新的 dashboard API
        const dashboardResponse = await dashboardAPI.getDashboardData();
        console.log('載入的儀表板數據:', dashboardResponse);
        console.log('月曆事件數據:', dashboardResponse.calendar_events);
        
        setDashboardData(dashboardResponse);
        setEquipmentStats(dashboardResponse.equipment_stats);
        setCalendarEvents(dashboardResponse.calendar_events);
        
        // 設定公告內容
        if (dashboardResponse.announcement && (
          dashboardResponse.announcement.maintenance_notice || 
          dashboardResponse.announcement.new_features || 
          dashboardResponse.announcement.usage_reminder
        )) {
          setAnnouncementData({
            maintenance_notice: dashboardResponse.announcement.maintenance_notice || '',
            new_features: dashboardResponse.announcement.new_features || '',
            usage_reminder: dashboardResponse.announcement.usage_reminder || ''
          });
        } else {
          // 如果沒有公告，直接跳過布告欄
          setShowAnnouncement(false);
          setIsLocked(false);
        }
      } catch (error: any) {
        console.error('載入儀表板數據失敗:', error);
        if (error.response) {
          console.error('HTTP 狀態:', error.response.status);
          console.error('錯誤詳情:', error.response.data);
        } else {
          console.error('網絡錯誤:', error.message);
        }
        // 如果獲取數據失敗，也跳過布告欄
        setShowAnnouncement(false);
        setIsLocked(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 月曆相關函數
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date: Date) => {
    const months = [
      '一月', '二月', '三月', '四月', '五月', '六月',
      '七月', '八月', '九月', '十月', '十一月', '十二月'
    ];
    return months[date.getMonth()];
  };

  // 檢查是否可以導航到指定方向
  const canNavigate = (direction: 'prev' | 'next') => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const prevMonth = currentDate.getMonth();
    const prevYear = currentDate.getFullYear();
    
    // 計算當前月份與今天月份的差距
    const monthDiff = (prevYear - currentYear) * 12 + (prevMonth - currentMonth);
    
    if (direction === 'prev') {
      return monthDiff > 0; // 只能從下個月回到這個月
    } else {
      return monthDiff < 1; // 只能從這個月到下個月
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (!canNavigate(direction)) {
      return; // 如果不能導航，直接返回
    }
    
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const handleAnnouncementConfirm = () => {
    setShowAnnouncement(false);
    setIsLocked(false);
  };

  const handleDateClick = (dateStr: string) => {
    const dayEvents = calendarEvents.filter(event => event.date === dateStr);
    
    // 排序：日段在前，夜段在後
    const sortedEvents = dayEvents.sort((a, b) => {
      if (a.time_period === '日' && b.time_period === '夜') return -1;
      if (a.time_period === '夜' && b.time_period === '日') return 1;
      return 0;
    });
    
    console.log('點擊日期:', dateStr);
    console.log('當日事件:', sortedEvents);
    console.log('事件車牌信息:', sortedEvents.map(e => ({ name: e.equipment_name, license_plate: e.license_plate })));
    setSelectedDate(dateStr);
    setSelectedDateEvents(sortedEvents);
    setShowDateDetail(true);
  };

  const renderCalendar = () => {
    const today = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // 獲取當前月份的第一天和最後一天
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // 獲取第一天是星期幾 (0=星期日, 1=星期一, ...)
    const firstDayOfWeek = firstDay.getDay();
    
    // 計算需要的行數
    const totalCells = firstDayOfWeek + daysInMonth;
    const rowsNeeded = Math.ceil(totalCells / 7);
    
    // 檢查是否可以導航
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    const canGoPrevious = !(currentMonth === todayMonth && currentYear === todayYear);
    const canGoNext = !(currentMonth === todayMonth + 1 && currentYear === todayYear);

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        border: '2px solid #e0e0e0',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        height: '100%'
      }}>
        {/* 月份標題和導航按鈕 */}
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            pointerEvents: 'none'
          }
        }}>
          {/* 上個月按鈕 */}
          <Button
            onClick={() => {
              if (canGoPrevious) {
                const newDate = new Date(currentDate);
                newDate.setMonth(currentDate.getMonth() - 1);
                setCurrentDate(newDate);
              }
            }}
            disabled={!canGoPrevious}
            sx={{
              color: canGoPrevious ? 'white' : 'rgba(255,255,255,0.5)',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: 2,
              background: canGoPrevious ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
              '&:hover': canGoPrevious ? {
                background: 'rgba(255,255,255,0.3)',
              } : {},
              '&:disabled': {
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.1)',
              },
              position: 'relative',
              zIndex: 1,
              cursor: canGoPrevious ? 'pointer' : 'not-allowed'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              ←
            </Typography>
          </Button>

          {/* 月份標題 */}
          <Typography variant="h5" sx={{ 
            color: 'white', 
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1
          }}>
            {currentYear} - {getMonthName(currentDate)}
          </Typography>

          {/* 下個月按鈕 */}
          <Button
            onClick={() => {
              if (canGoNext) {
                const newDate = new Date(currentDate);
                newDate.setMonth(currentDate.getMonth() + 1);
                setCurrentDate(newDate);
              }
            }}
            disabled={!canGoNext}
            sx={{
              color: canGoNext ? 'white' : 'rgba(255,255,255,0.5)',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: 2,
              background: canGoNext ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
              '&:hover': canGoNext ? {
                background: 'rgba(255,255,255,0.3)',
              } : {},
              '&:disabled': {
                color: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.1)',
              },
              position: 'relative',
              zIndex: 1,
              cursor: canGoNext ? 'pointer' : 'not-allowed'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              →
            </Typography>
          </Button>
        </Box>
        
        {/* 星期標題 */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)',
          background: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {['日', '一', '二', '三', '四', '五', '六'].map(day => (
            <Box key={day} sx={{ 
              p: 1.5, 
              textAlign: 'center', 
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderTop: 'none',
              borderBottom: 'none',
              '&:first-of-type': {
                borderLeft: 'none'
              },
              '&:last-of-type': {
                borderRight: 'none'
              }
            }}>
              {day}
            </Box>
          ))}
        </Box>
        
        {/* 日期網格 */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridTemplateRows: `repeat(${rowsNeeded}, 1fr)`,
          flex: 1,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          minHeight: '500px',
          width: '100%'
        }}>
          {/* 空白格子（上個月的最後幾天） */}
          {Array.from({ length: firstDayOfWeek }, (_, i) => (
            <Box key={`prev-${i}`} sx={{ 
              border: '1px solid #e0e0e0',
              background: '#f9f9f9',
              opacity: 0.3
            }} />
          ))}
          
          {/* 當前月份的日期 */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const cellDate = new Date(currentYear, currentMonth, day);
            const isToday = 
              day === today.getDate() && 
              currentMonth === today.getMonth() && 
              currentYear === today.getFullYear();
            const isPast = cellDate < today && !isToday;
            
            const currentDateStr = `${cellDate.getFullYear()}-${(cellDate.getMonth() + 1).toString().padStart(2, '0')}-${cellDate.getDate().toString().padStart(2, '0')}`;
            
            // 過去的日期不顯示任何預約事件
            const dayEvents = isPast ? [] : calendarEvents.filter(event => event.date === currentDateStr);
            
            // 分離日段和夜段事件
            const dayPeriodEvents = dayEvents.filter(event => event.time_period === '日');
            const nightPeriodEvents = dayEvents.filter(event => event.time_period === '夜');
            
            // 合併顯示：先顯示日段，再顯示夜段
            const allEvents = [...dayPeriodEvents, ...nightPeriodEvents];
            const maxDisplay = 2; // 網格中最多顯示2個（日段優先，然後夜段）
            const limitedDisplayEvents = allEvents.slice(0, maxDisplay);
            const hasMore = allEvents.length > maxDisplay;

            return (
              <Box
                key={day}
                onClick={() => !isPast && handleDateClick(currentDateStr)}
                sx={{
                  border: '1px solid #e0e0e0',
                  p: 2,
                  background: isToday 
                    ? 'linear-gradient(135deg, #bbdefb 0%, #c5cae9 100%)'
                    : isPast
                    ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  color: isToday ? 'white' : isPast ? '#999' : '#333',
                  opacity: isPast ? 0.6 : 1,
                  cursor: isPast ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  '&:hover': !isPast ? {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    zIndex: 1
                  } : {}
                }}
              >
                {/* 左邊：日期數字 */}
                <Box sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: isToday ? 'white' : isPast ? '#999' : '#333',
                  minWidth: '32px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '32px',
                  borderRadius: '50%',
                  background: isToday 
                    ? 'rgba(33,150,243,0.4)' 
                    : isPast 
                    ? 'rgba(153,153,153,0.2)' 
                    : 'rgba(51,51,51,0.1)'
                }}>
                  {day}
                </Box>
                
                {/* 右邊：預約事件 */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                         {limitedDisplayEvents.map((event, index) => (
                           <Box
                             key={index}
                             sx={{
                               fontSize: '0.85rem',
                               color: event.time_period === '日' ? '#2196f3' : '#ff9800',
                               fontWeight: 'bold',
                               px: 0.8,
                               py: 0.4,
                               borderRadius: 0.8,
                               background: event.time_period === '日' ? 'rgba(33,150,243,0.1)' : 'rgba(255,152,0,0.1)',
                               border: `1px solid ${event.time_period === '日' ? '#2196f3' : '#ff9800'}`,
                               display: 'inline-block',
                               width: 'fit-content',
                               maxWidth: '100%',
                               overflow: 'hidden',
                               textOverflow: 'ellipsis',
                               whiteSpace: 'nowrap'
                             }}
                           >
                             {event.display_text}
                           </Box>
                         ))}
                         {hasMore && (
                           <Box
                             sx={{
                               fontSize: '0.75rem',
                               color: '#666',
                               fontWeight: 'bold',
                               textAlign: 'center',
                               py: 0.3,
                               px: 0.5,
                               background: 'rgba(102,102,102,0.1)',
                               borderRadius: 0.8,
                               border: '1px solid #666'
                             }}
                           >
                             +{allEvents.length - maxDisplay}
                           </Box>
                         )}
                </Box>
              </Box>
            );
          })}
          
          {/* 填充剩餘格子 */}
          {Array.from({ length: (rowsNeeded * 7) - firstDayOfWeek - daysInMonth }, (_, i) => (
            <Box key={`next-${i}`} sx={{ 
              border: '1px solid #e0e0e0',
              background: '#f9f9f9',
              opacity: 0.3
            }} />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0.5 }}>
      {/* 布告欄對話框 */}
      <Dialog 
        open={showAnnouncement} 
        onClose={() => {}} // 不允許點擊外部關閉
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          color: 'white',
          textAlign: 'center',
          fontSize: '1.8rem',
          fontWeight: 'bold',
          py: 4,
          mb: 0
        }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Announcement sx={{ fontSize: '2.5rem' }} />
            重要公告
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#fafafa' }}>
          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 4 }}>
            {/* 系統維護通知 - 只在有內容時顯示 */}
            {announcementData.maintenance_notice && (
              <Card sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: '2px solid #ff6b35',
                backgroundColor: '#fff5f5'
              }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Warning sx={{ color: '#ff6b35', fontSize: '1.5rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    系統維護通知
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, color: '#333' }}>
                  {announcementData.maintenance_notice}
                </Typography>
              </Card>
            )}

            {/* 新功能上線 - 只在有內容時顯示 */}
            {announcementData.new_features && (
              <Card sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: '2px solid #2196f3',
                backgroundColor: '#f3f8ff'
              }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <CheckCircle sx={{ color: '#2196f3', fontSize: '1.5rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    新功能上線
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, color: '#333' }}>
                  {announcementData.new_features}
                </Typography>
              </Card>
            )}

            {/* 使用提醒 - 只在有內容時顯示 */}
            {announcementData.usage_reminder && (
              <Card sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: '2px solid #4caf50',
                backgroundColor: '#f1f8e9'
              }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Assignment sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                    使用提醒
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, color: '#333' }}>
                  {announcementData.usage_reminder}
      </Typography>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5', justifyContent: 'center' }}>
          <Button 
            onClick={handleAnnouncementConfirm}
            variant="contained"
            size="large"
            autoFocus
            sx={{ 
              borderRadius: 2, 
              px: 6, 
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
              }
            }}
          >
            我已了解，進入頁面
          </Button>
        </DialogActions>
      </Dialog>

      {/* 主要內容區 - 左右布局 */}
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr',
            gap: 2,
            alignItems: 'start',
            opacity: isLocked ? 0.3 : 1,
            pointerEvents: isLocked ? 'none' : 'auto',
            transition: 'all 0.3s ease',
            width: '100%'
          }}
        >
        {/* 右側月曆區 */}
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid rgba(255,255,255,0.2)',
              overflow: 'hidden',
              width: '100%'
            }}
          >
        <CardContent sx={{ p: 0 }}>
          {/* 月曆內容 */}
          {renderCalendar()}
        </CardContent>
      </Card>
        </Box>
      </Box>

      {/* 鎖定狀態覆蓋層 */}
      {isLocked && (
        <Backdrop
          open={isLocked}
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(2px)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              p: 4,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CircularProgress size={60} sx={{ color: '#1976d2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
              請先閱讀重要公告
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
              點擊上方「我已了解，進入儀錶板」按鈕後即可使用
            </Typography>
          </Box>
        </Backdrop>
      )}

      {/* 日期詳情彈窗 */}
      <Dialog
        open={showDateDetail}
        onClose={() => setShowDateDetail(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold',
          textAlign: 'center',
          py: 2
        }}>
          {selectedDate} 預約詳情
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedDateEvents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                此日期無預約記錄
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>設備名稱</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>車牌/編號</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>部門</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>類型</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>時段</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDateEvents.map((event, index) => (
                    <tr key={index} style={{ 
                      background: index % 2 === 0 ? '#ffffff' : '#f9f9f9'
                    }}>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0', fontWeight: 'bold' }}>
                        {event.equipment_name}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0' }}>
                        {event.license_plate || '無'}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0' }}>
                        {event.department_name}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0' }}>
                        {event.equipment_type === 'vehicle' ? '車輛' : 
                         event.equipment_type === 'equipment' ? '機具' : '未知'}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0' }}>
                        <Chip 
                          label={event.time_period}
                          size="small"
                          sx={{ 
                            fontWeight: 'bold',
                            backgroundColor: event.time_period === '日' ? '#2196f3' : '#ff9800',
                            color: 'white'
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e0e0e0' }}>
                        <Chip 
                          label={event.status === 'reserved' ? '預約中' : 
                                 event.status === 'ready' ? '準備就緒' :
                                 event.status === 'active' ? '使用中' : event.status}
                          color={event.status === 'reserved' ? 'warning' : 
                                 event.status === 'ready' ? 'success' :
                                 event.status === 'active' ? 'info' : 'default'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => setShowDateDetail(false)}
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            關閉
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
